import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { adminApi, API } from "../../lib/api";
import { toast } from "sonner";

const STATUSES = ["pending", "paid", "failed", "expired", "refunded"];

const BADGE = {
  pending: "bg-amber-100 text-amber-700",
  paid: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  expired: "bg-zinc-100 text-zinc-600",
  refunded: "bg-blue-100 text-blue-700",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const load = () => adminApi.get("/orders", { status, search }).then((r) => setOrders(r.orders));
  useEffect(() => { load().catch(() => {}); }, [status]); // eslint-disable-line

  const markPaid = async (o) => {
    await adminApi.patch(`/orders/${o.id}`, { status: "paid", payment_ref: o.payment_ref || "manual/offline" });
    toast.success(`Order ${o.order_number} marked paid.`);
    load();
  };

  const verifyPayment = async (o) => {
    try {
      const res = await adminApi.post(`/orders/${o.id}/verify-payment`, {});
      toast.success(res.message || `Status: ${res.status}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    }
  };

  const refundOrder = async (o) => {
    if (!window.confirm(`Refund order ${o.order_number} (₹${(o.amount || 0).toLocaleString("en-IN")})? Reader access will be revoked.`)) return;
    try {
      const res = await adminApi.post(`/orders/${o.id}/refund`, {});
      toast.success(res.gateway_refund ? "Refunded via gateway." : "Marked refunded (manual).");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refund failed");
    }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("bm_admin_token");
    const res = await fetch(`${API}/apadmin/orders-export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "bookmala-orders.csv";
    a.click();
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-xl font-bold text-zinc-900">Orders & Subscriptions</h1>
        <button onClick={exportCsv} data-testid="export-csv-btn"
          className="inline-flex items-center gap-1.5 border border-zinc-300 text-sm font-semibold px-4 py-2 rounded-md hover:border-zinc-900">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>
      <div className="flex gap-3 mb-4 flex-wrap">
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="orders-status-filter"
          className="border border-zinc-300 rounded-md px-3 py-2 text-sm bg-white">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Search order / subscriber / magazine" data-testid="orders-search-input"
          className="border border-zinc-300 rounded-md px-3 py-2 text-sm flex-1 min-w-[220px]" />
        <button onClick={load} data-testid="orders-search-btn" className="bg-zinc-900 text-white text-sm font-semibold px-4 py-2 rounded-md">Search</button>
      </div>
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="orders-table">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-200">
              <th className="px-4 py-3">Order</th><th className="px-4 py-3">Subscriber</th><th className="px-4 py-3">Magazine / Plan</th>
              <th className="px-4 py-3">Amount</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Payment Ref</th>
              <th className="px-4 py-3">Date</th><th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="border-b border-zinc-100 last:border-0" data-testid={`order-row-${o.order_number}`}>
                <td className="px-4 py-3 font-mono text-xs">{o.order_number}</td>
                <td className="px-4 py-3">{o.subscriber?.name}<div className="text-xs text-zinc-400">{o.subscriber?.email}</div></td>
                <td className="px-4 py-3">{o.magazine_title}<div className="text-xs text-zinc-400">{o.plan_name}</div></td>
                <td className="px-4 py-3">₹{(o.amount || 0).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${BADGE[o.status] || "bg-zinc-100 text-zinc-600"}`}>{o.status}</span>
                  {o.failure_reason && <div className="text-[10px] text-red-600 mt-1 max-w-[160px] truncate" title={o.failure_reason}>{o.failure_reason}</div>}
                </td>
                <td className="px-4 py-3 font-mono text-xs text-zinc-500">{o.payment_ref || "—"}</td>
                <td className="px-4 py-3 text-xs text-zinc-500">{(o.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    {(o.status === "pending" || o.status === "failed") && (
                      <>
                        <button onClick={() => verifyPayment(o)} data-testid={`verify-payment-${o.order_number}`}
                          className="text-xs font-semibold text-blue-700 border border-blue-300 rounded-md px-2.5 py-1 hover:bg-blue-50">
                          Verify
                        </button>
                        <button onClick={() => markPaid(o)} data-testid={`mark-paid-${o.order_number}`}
                          className="text-xs font-semibold text-green-700 border border-green-300 rounded-md px-2.5 py-1 hover:bg-green-50">
                          Mark paid
                        </button>
                      </>
                    )}
                    {o.status === "paid" && (
                      <button onClick={() => refundOrder(o)} data-testid={`refund-${o.order_number}`}
                        className="text-xs font-semibold text-red-700 border border-red-300 rounded-md px-2.5 py-1 hover:bg-red-50">
                        Refund
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!orders.length && <p className="text-sm text-zinc-500 text-center py-10">No orders found.</p>}
      </div>
    </div>
  );
}
